import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { readCookie } from "../../helpers/cookie-helper";
import { stringToObject } from "../../helpers/object-helper";
import { show as showSnackbar } from "./snackbar";

export interface PhoneState {
  SIPAccount: {
    id: string;
    name: string;
    status: string;
    ip: string;
    userId: string;
    pbxId: string;
  };
  contacts: {
    customer: {
      id: string;
      code: string;
      fullname: string;
      phonenumber: string;
    };
    type: string;
    fullname: string;
    phonenumber: string;
  }[];
  contact: {
    id: string;
    fullname: string;
    customizedFields: any;
    note: string;
    result: string;
    isDone: boolean;
  };
  campaign: {
    id: string;
    name: string;
  };
  call: {
    direction: string;
    fromNumber: string;
    fromSIPAccountId: string;
    fromUserId: string;
    toNumber: string;
    toSIPAccountId: string;
    toUserId: string;
    startedAt: number;
    answeredAt: number;
    duration: number;
    answeredDuration: number;
    state: string;
    campaignId: string;
    campaignContactId: string;
  };
  loadingSIPAccountStatus: "none" | "loading" | "failed" | "done";
}

const initialState: PhoneState = {
  SIPAccount: {
    id: "",
    name: "",
    status: "offline",
    ip: "",
    userId: "",
    pbxId: "",
  },
  contacts: [],
  contact: {
    id: "",
    fullname: "",
    customizedFields: {},
    note: "",
    result: "",
    isDone: false,
  },
  campaign: {
    id: "",
    name: "",
  },
  call: {
    direction: "",
    fromNumber: "",
    fromSIPAccountId: "",
    fromUserId: "",
    toNumber: "",
    toSIPAccountId: "",
    toUserId: "",
    startedAt: 0,
    answeredAt: 0,
    duration: 0,
    answeredDuration: 0,
    state: "",
    campaignId: "",
    campaignContactId: "",
  },
  loadingSIPAccountStatus: "none",
};

export const phoneSlice = createSlice({
  name: "phone",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearCall: (state: PhoneState) => {
      if (state.call.state === "hangup") {
        state.call = initialState.call;
        state.contact = initialState.contact;
        state.campaign = initialState.campaign;
      }
    },
    setCall: (state: PhoneState, action: PayloadAction<PhoneState["call"]>) => {
      state.call = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSIPAccount.pending, (state) => {
        state.loadingSIPAccountStatus = "loading";
      })
      .addCase(loadSIPAccount.fulfilled, (state, action) => {
        state.loadingSIPAccountStatus = "done";
        state.SIPAccount.id = action.payload.id;
        state.SIPAccount.name = action.payload.name;
        state.SIPAccount.ip = action.payload.ip || "";
        state.SIPAccount.userId = action.payload.userId;
        state.SIPAccount.status = action.payload.status || "offline";
        state.SIPAccount.pbxId = action.payload.pbxId;
      })
      .addCase(loadSIPAccount.rejected, (state) => {
        state.loadingSIPAccountStatus = "failed";
      })
      .addCase(loadCampaign.fulfilled, (state, action) => {
        if (action.payload) state.campaign = action.payload;
      })
      .addCase(loadCampaignContact.fulfilled, (state, action) => {
        if (action.payload) state.contact = action.payload;
      })
      .addCase(loadContacts.fulfilled, (state, action) => {
        if (action.payload) state.contacts = action.payload;
      });
  },
});

// Actions

export const { setCall, clearCall } = phoneSlice.actions;

// ----------------------------------------------------------------------

export const loadSIPAccount = createAsyncThunk(
  "phone/loadSIPAccount",
  async () => {
    const cookieValue = readCookie("user");
    const user = stringToObject(cookieValue);
    const accessToken = user ? user.accessToken : "";
    const apiURL = `${process.env.REACT_APP_VOICE_API}/sip-accounts?userId=${user.id}&pageIndex=0&pageSize=1`;
    const axiosConfig = {
      method: "get",
      url: apiURL,
      headers: {
        Authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      data: {},
    };
    const response = await axios(axiosConfig);
    if (response.data.data && response.data.data.length) {
      return response.data.data[0];
    } else {
      return initialState.SIPAccount;
    }
  }
);

export const loadContacts = createAsyncThunk(
  "phone/loadContacts",
  async (phonenumber: string, { dispatch, getState }) => {
    const state = getState();
    const phone = (state as any).phone;
    if (phone.call.fromNumber !== "" && phone.call.direction === "inbound") {
      const cookieValue = readCookie("user");
      const user = stringToObject(cookieValue);
      const accessToken = user ? user.accessToken : "";
      const apiURL = `${process.env.REACT_APP_LCM_API}/data-handler/query-contacts?phone=${phonenumber}`;
      const axiosConfig = {
        method: "post",
        url: apiURL,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: {},
      };
      const response = await axios(axiosConfig);
      if (response.data) {
        return response.data;
      } else {
        return initialState.contacts;
      }
    } else {
      return [];
    }
  }
);

export const loadCampaign = createAsyncThunk(
  "phone/loadCampaign",
  async (id: string, { dispatch, getState }) => {
    const state = getState();
    if ((state as any).phone.campaign.id !== id) {
      const cookieValue = readCookie("user");
      const user = stringToObject(cookieValue);
      const accessToken = user ? user.accessToken : "";
      const apiURL = `${process.env.REACT_APP_SALES_API}/campaigns/${id}`;
      const axiosConfig = {
        method: "get",
        url: apiURL,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: {},
      };
      const response = await axios(axiosConfig);
      if (response.data) {
        return response.data;
      } else {
        return initialState.campaign;
      }
    } else {
      return undefined;
    }
  }
);

export const loadCampaignContact = createAsyncThunk(
  "phone/loadCampaignContact",
  async (id: string, { dispatch, getState }) => {
    const state = getState();
    if ((state as any).phone.contact.id !== id) {
      const cookieValue = readCookie("user");
      const user = stringToObject(cookieValue);
      const accessToken = user ? user.accessToken : "";
      const apiURL = `${process.env.REACT_APP_SALES_API}/campaign-contacts/${id}`;
      const axiosConfig = {
        method: "get",
        url: apiURL,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: {},
      };
      const response = await axios(axiosConfig);
      if (response.data) {
        return response.data;
      } else {
        return initialState.contact;
      }
    } else {
      return undefined;
    }
  }
);

export const saveCampaignContact = createAsyncThunk(
  "phone/saveCampaignContact",
  async (data: any, { dispatch }) => {
    const cookieValue = readCookie("user");
    const user = stringToObject(cookieValue);
    const accessToken = user ? user.accessToken : "";
    const apiURL = `${process.env.REACT_APP_SALES_API}/campaign-contacts/${data.id}`;
    const axiosConfig = {
      method: "patch",
      url: apiURL,
      headers: {
        Authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      data: {
        note: data.note,
        result: data.result,
        resultCategory: data.resultCategory,
        isDone: data.isDone,
        userId: data.userId,
        customizedFields: data.customizedFields,
      },
    };

    const response = await axios(axiosConfig);
    if (response.data) {
      dispatch(
        showSnackbar({ message: "Thao tác thành công!", type: "success" })
      );
      dispatch(clearCall());
    } else {
      dispatch(showSnackbar({ message: "Thao tác lỗi!", type: "error" }));
    }
  }
);

export default phoneSlice.reducer;
